import { Dialog, makeStyles } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import axios from 'axios';
import { add, differenceInCalendarMonths, endOfDay, startOfDay, sub } from 'date-fns';
import React from 'react';
import { Save, X } from 'react-feather';
import { usePrefixTranslation } from '../../../i18n';
import { theme } from '../../../theme/theme';
import { Area, AssetType, CostCenter } from '../../../types/asset';
import { reportFactory } from '../../../types/factory';
import { Report } from '../../../types/report';
import { Button } from '../../global/forms/Button';
import { DatePicker } from '../../global/forms/DateTime';
import { Input } from '../../global/forms/Input';
import { Select } from '../../global/forms/Select';

const useReportEditorClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        width: 560,
        gap: 16,
        padding: '40px 40px 24px 40px',
        color: theme.colors.neutral[1],
        '& > span:first-child': { ...theme.fonts.h4 },
        '& > span:nth-child(2)': { ...theme.fonts.label },
    },
    row: {
        display: 'flex',
        gap: 16,
        '& > div': { flex: 1 },
    },
    buttons: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 16,
    },
    item: {
        textTransform: 'capitalize',
        whiteSpace: 'nowrap',
    },
});

/**
 * Dialog to edit a report.
 * This component does not create and update reports on the backend by its own, it only changes a given report instance
 * or creates a local instance of a report.
 *
 * @param props.loading indicate a loading state
 * @param props.report initial report to edit
 * @param props.mode if the dialog show labels and buttons for creation or update of a report
 * @param props.open set the dialog display state
 * @param props.onClose callback triggered when the user clicks on the background, if not provided, onCancel is called
 * @param props.onCancel callback triggered when the user clicks on the close button
 * @param props.onSave callback triggered when the user clicks on the save button, the report is passed as an argument
 */
export const ReportEditor = (props: {
    loading?: boolean;
    report?: Report;
    mode: 'create' | 'update';
    open: boolean;
    onClose?: () => void;
    onCancel?: () => void;
    onSave: (report: Report) => void;
}) => {
    const classes = useReportEditorClasses();
    const { t } = usePrefixTranslation('report', { report: 'dialogs.ReportEditor' });
    const report = React.useRef(props.report ?? reportFactory());
    const update = React.useReducer(i => i + 1, 0)[1];
    const templates = ['yearly', 'quarterly', 'monthly'] as const;
    const initialMonths = differenceInCalendarMonths(report.current.end_date, report.current.start_date);
    const initialTemplate = initialMonths <= 1 ? 'monthly' : initialMonths <= 3 ? 'quarterly' : 'yearly';
    const [template, setTemplate] = React.useState<typeof templates[number]>(initialTemplate);
    const months = template === 'yearly' ? 12 : template === 'quarterly' ? 3 : 1;
    const [types, setTypes] = React.useState<AssetType[]>([]);
    const [areas, setAreas] = React.useState<Area[]>([]);
    const [costCenters, setCostCenters] = React.useState<CostCenter[]>([]);

    React.useEffect(() => {
        axios.get<AssetType[]>('/asset-types').then(response => setTypes(response.data));
        axios.get<Area[]>('/areas').then(response => setAreas(response.data));
        axios.get<CostCenter[]>('/cost-centers').then(response => setCostCenters(response.data));
    }, []);

    React.useLayoutEffect(() => fixDates(months, 'end'), [months]);
    React.useLayoutEffect(() => fixDates(months, 'end'), [+report.current.start_date]);
    React.useLayoutEffect(() => fixDates(months, 'start'), [+report.current.end_date]);

    const fixDates = (months: number, fix: 'start' | 'end') => {
        if (fix === 'end') {
            report.current.start_date = startOfDay(report.current.start_date);
            report.current.end_date = endOfDay(add(report.current.start_date, { months, days: -1 }));
        } else {
            report.current.end_date = endOfDay(report.current.end_date);
            report.current.start_date = startOfDay(sub(report.current.end_date, { months, days: -1 }));
        }
        update();
    };

    if (props.loading)
        return (
            <Dialog classes={{ paper: classes.root }} open={props.open} onClose={props.onClose ?? props.onCancel}>
                <Skeleton variant='text' width='30%' />
                <Skeleton variant='text' width='60%' />
                <Skeleton variant='rect' height='78px' />
                <Skeleton variant='rect' height='78px' />
                <div className={classes.row}>
                    <Skeleton variant='rect' height='78px' style={{ flex: 1 }} />
                    <Skeleton variant='rect' height='78px' style={{ flex: 1 }} />
                </div>
                <Skeleton variant='rect' height='78px' />
                <Skeleton variant='rect' height='78px' />
                <Skeleton variant='rect' height='78px' />
                <div className={classes.buttons}>
                    <Skeleton variant='rect' width='150px' height='50px' />
                    <Skeleton variant='rect' width='150px' height='50px' />
                </div>
            </Dialog>
        );

    return (
        <Dialog classes={{ paper: classes.root }} open={props.open} onClose={props.onClose ?? props.onCancel}>
            <span>{t(`title-${props.mode}`)}</span>
            <span>{t(`subtitle-${props.mode}`)}</span>
            <Input
                theme='primary'
                label={t('label-title')}
                placeholder={t('placeholder-title')}
                value={report.current.title}
                onChange={e => ((report.current.title = e.target.value), update())}
            />
            <Select
                theme='primary'
                label={t('label-template')}
                placeholder={t('placeholder-template')}
                options={templates.map(tp => t(`template-${tp}`))}
                selected={templates.map(tp => tp === template)}
                onSelected={selected => setTemplate(templates.filter((_, i) => selected[i])[0])}
            />
            <div className={classes.row}>
                {(['start', 'end'] as const).map(range => (
                    <DatePicker
                        key={range}
                        theme='primary'
                        label={t(`label-date-${range}`)}
                        placeholder={t(`placeholder-date-${range}`)}
                        date={report.current[`${range}_date` as const]}
                        onDate={date => date && ((report.current[`${range}_date` as const] = date), update())}
                    />
                ))}
            </div>
            <hr style={{ borderColor: theme.colors.neutral[9] }} />
            {[
                { rel: 'types', prop: 'asset_type_filters', options: types },
                { rel: 'areas', prop: 'area_filters', options: areas },
                { rel: 'cost-centers', prop: 'cost_center_filters', options: costCenters },
            ].map(({ rel, prop, options }) => (
                <Select
                    theme='primary'
                    key={rel}
                    label={t(`label-${rel}`)}
                    placeholder={t(`placeholder-${rel}`)}
                    searchLabel={t(`label-${rel}-search`)}
                    render={option => <span className={classes.item}>{option.name.toLowerCase()}</span>}
                    search={item => item.name}
                    multi
                    options={options}
                    selected={options.map(option => (report.current as any)[prop].includes(option.id))}
                    onSelected={selected => {
                        (report.current as any)[prop] = options.filter((_, i) => selected[i]).map(option => option.id);
                        update();
                    }}
                />
            ))}
            <div className={classes.buttons}>
                <Button theme='light' onClick={props.onCancel}>
                    <X /> {t('button-cancel')}
                </Button>
                <Button
                    theme='primary'
                    disabled={report.current.title.length === 0}
                    onClick={() => ((report.current.created_date = new Date()), props.onSave(report.current))}
                >
                    <Save /> {t(`button-save-${props.mode}`)}
                </Button>
            </div>
        </Dialog>
    );
};
